import { createSelector, createStructuredSelector } from 'reselect';
import { getUser, getAmplitudeInstance } from '../../modules/session/getters';
import { getHasPermissionForReports } from '../account-navbar/getters';
import { getDoesNotHavePermissionForReports } from '../account-navbar/getters';
import { getIsViewingOwnAccount } from '../account-navbar/getters';
import { isBreakdownTypeAvailable } from '../../lib/report-utils';
import moment from 'moment';

// Reports Home - Quick Reports, etc.
export const getReportFileOptions = (state) => state.reports.home.fileFormats;
export const getShowCustomReportModal = (state) => state.reports.home.showCustomReportDeleteModal;
export const getShowLargeReportEmailModal = (state) => state.reports.home.showLargeReportEmailModal;
export const getShowQuickReportModal = (state) => state.reports.home.showQuickReportModal;
export const getReportSelected = (state) => state.reports.home.quickReports.reportSelected;
export const getCustomReportSelected = (state) => state.reports.home.customReports.reportSelected;
export const getIncludeExtendedAudit = (state) => state.reports.home.includeExtendedAudit;
export const getReportCurrencySelected = (state) => state.reports.home.quickReports.selectedCurrency;
export const getReportBreakdownTypeSelected = (state) => {
  const { title } = getReportSelected(state);

  return isBreakdownTypeAvailable(title) ? state.reports.home.quickReports.breakdownTypeSelected : null;
};
export const getReportOptions = (state) => state.reports.home.quickReports.options;
const getReportTimeRangeSelected = (state) => state.reports.home.quickReports.timeRangeSelected;
const getReportCustomTimeRange = (state) => state.reports.home.quickReports.customTimeRange;
const getReportSingleDate = (state) => state.reports.home.quickReports.singleDate;
const getReportFocusedInput = (state) => state.reports.home.quickReports.focusedInput;
const getCustomReportsPagination = (state) => state.reports.home.customReports.pagination;
const getCustomReportsSearch = (state) => state.reports.home.customReports.searchText;

// Reports Home - EventSelect modal
export const getSelectedEvents = (state) => state.reports.selectedEvents.events;
export const getSelectedEventIDs = (state) => state.reports.selectedEvents.ids;
export const getCustomSelectedEvents = (state) => state.reports.selectedEventsModal.events;
export const getCustomSelectedIDsFromModal = (state) => state.reports.selectedEventsModal.selected;
const getSelectedTime = (state) => state.reports.selectedTime.selectedTime;
const getSelectedEventsModalIsOpen = (state) => state.reports.selectedEventsModal.isOpen;
const getOrganizationCurrencies = (state) =>  state.reports.selectedOrganization.currencies;
const getOwnerId = (state) =>  state.reports.selectedOrganization.ownerId;

// Custom Reports - General
export const getValidator = (state) => state.reports.customGeneral.validator;
const getCustomReport = (state) => state.reports.customGeneral;
const getReportLoading = (state) => state.reports.customGeneral.loading;

// Custom Reports - Filters + Sorts
export const getFilterValidator = (state) => state.reports.customFilters.validator;
const getCustomFilters = (state) => state.reports.customFilters;
const getCustomSorts = (state) => state.reports.customSorts;
const getCustomTimeRange = (state) => state.reports.selectedTime.customTimeRange;
const getTempTimeRange = (state) => state.reports.selectedTime.tempTimeRange;
const getSelectedTimeFocusedInput = (state) => state.reports.selectedTime.focusedInput;
const getSelectedCurrency = (state) => state.reports.selectedCurrency;

// Custom Reports - Scheduler
const getCustomReportScheduler = (state) => state.reports.customScheduler;

// Custom Reports - Date Select modals
const getDateOfOrderTimeRangeSelected = (state) => state.reports.customFilters.data.dateOfOrder.timeRangeSelected;
const getDateOfAttendingTimeRangeSelected = (state) => state.reports.customFilters.data.dateOfAttending.timeRangeSelected;
const getDateOfOrderCustomTimeRange = (state) => state.reports.customFilters.data.dateOfOrder.customTimeRange;
const getDateOfAttendingCustomTimeRange = (state) => state.reports.customFilters.data.dateOfAttending.customTimeRange;
const getDateOfOrderSingleDate = (state) => state.reports.customFilters.data.dateOfOrder.singleDate;
const getDateOfAttendingSingleDate = (state) => state.reports.customFilters.data.dateOfAttending.singleDate;
const getDateOfOrderFocusedInput = (state) => state.reports.customFilters.data.dateOfOrder.focusedInput;
const getDateOfAttendingFocusedInput = (state) => state.reports.customFilters.data.dateOfAttending.focusedInput;

export const PAST_7_DAYS = 'wtd';
export const PAST_30_DAYS = 'mtd';
export const YEAR_TO_DATE = 'ytd';
export const CUSTOM = 'custom';

// Selectors
function getTimeRange(timeRange, customRange, singleDate) {
  if (timeRange === PAST_30_DAYS) {
    const monthAgo = moment().subtract(1, 'month').format('YYYY-MM-DD');
    const now = moment().format('YYYY-MM-DD');
    return `?from=${monthAgo}&to=${now}`;
  }
  else if (timeRange === PAST_7_DAYS) {
    const weekAgo = moment().subtract(1, 'week').format('YYYY-MM-DD');
    const now = moment().format('YYYY-MM-DD');
    return `?from=${weekAgo}&to=${now}`;
  }
  else if (timeRange === 'custom') {
    const from = moment(customRange.startDate).format('YYYY-MM-DD');
    const to = moment(customRange.endDate).format('YYYY-MM-DD');
    return `?from=${from}&to=${to}`;
  }
  else if (timeRange === 'single') {
    const from = moment(singleDate).format('YYYY-MM-DD');
    const to = moment(singleDate).format('YYYY-MM-DD');
    return `?from=${from}&to=${to}`;
  }
  else if (timeRange == 'before') {
    const from = moment().subtract(7, 'year').format('YYYY-MM-DD');
    const to = moment(singleDate).format('YYYY-MM-DD');
    return `?from=${from}&to=${to}`;
  }
  else if (timeRange == 'after') {
    const from = moment(singleDate).format('YYYY-MM-DD');
    const to = moment().add(1, 'day').format('YYYY-MM-DD');
    return `?from=${from}&to=${to}`;
  }
  return '?'; // default (all transactions)
}

function getAfterDateStr(singleDate) {
  return 'After ' + moment(singleDate).format('LL');
}

function getBeforeDateStr(singleDate) {
  return 'Before ' + moment(singleDate).format('LL');
}

function getCustomDateStr(customRange) {
  return moment(customRange.startDate).format('LL') + ' - ' +
    moment(customRange.endDate).format('LL');
}

function getSingleDateStr(singleDate) {
  return moment(singleDate).format('LL');
}

export const getReportTimeRange = createSelector(
  [getReportTimeRangeSelected, getReportCustomTimeRange, getReportSingleDate],
  (timeRange, customRange, singleDate) => getTimeRange(timeRange, customRange, singleDate)
);
export const getDateOfOrderTimeRange = createSelector(
  [getDateOfOrderTimeRangeSelected, getDateOfOrderCustomTimeRange, getDateOfOrderSingleDate],
  (timeRange, customRange, singleDate) => getTimeRange(timeRange, customRange, singleDate)
);
export const getDateOfAttendingTimeRange = createSelector(
  [getDateOfAttendingTimeRangeSelected, getDateOfAttendingCustomTimeRange, getDateOfAttendingSingleDate],
  (timeRange, customRange, singleDate) => getTimeRange(timeRange, customRange, singleDate)
);
const getCustomReportDateStr = createSelector(
  [getReportCustomTimeRange],
  (customRange) => getCustomDateStr(customRange)
);
const getDateOfOrderCustomDateStr = createSelector(
  [getDateOfOrderCustomTimeRange],
  (customRange) => getCustomDateStr(customRange)
);
const getDateOfAttendingCustomDateStr = createSelector(
  [getDateOfAttendingCustomTimeRange],
  (customRange) => getCustomDateStr(customRange)
);
const getSingleReportDateStr = createSelector(
  [getReportSingleDate],
  (singleDate) => getSingleDateStr(singleDate)
);
const getDateOfOrderSingleDateStr = createSelector(
  [getDateOfOrderSingleDate],
  (singleDate) => getSingleDateStr(singleDate)
);
const getDateOfAttendingSingleDateStr = createSelector(
  [getDateOfAttendingSingleDate],
  (singleDate) => getSingleDateStr(singleDate)
);
const getDateOfOrderAfterDateStr = createSelector(
  [getDateOfOrderSingleDate],
  (singleDate) => getAfterDateStr(singleDate)
);
const getDateOfAttendingAfterDateStr = createSelector(
  [getDateOfAttendingSingleDate],
  (singleDate) => getAfterDateStr(singleDate)
);
const getDateOfOrderBeforeDateStr = createSelector(
  [getDateOfOrderSingleDate],
  (singleDate) => getBeforeDateStr(singleDate)
);
const getDateOfAttendingBeforeDateStr = createSelector(
  [getDateOfAttendingSingleDate],
  (singleDate) => getBeforeDateStr(singleDate)
);


// Event Select modal
const getHostId = createSelector(
  [getUser, getOwnerId],
  (user, hostId) => {
    if (hostId !== '') {
      return hostId;
    }
    else if (user) {
      return user.id;
    }
    return null;
  }
);

// Privacy Policy Provided by user
const getIsPrivacyPolicyProvided = createSelector(
  [getUser],
  (user) => {
    if (user.privacy_policy_url) return true;
    return false;
  }
);

// Waitlist Fan Opt in Feture Flag
const getIsWaitlistOptInEnabled = createSelector(
  [getUser],
  (user) => { return user.waitlist_fan_opt_in; }
);

export const getDateFilter = createSelector(
  [getSelectedTime, getCustomTimeRange],
  (selectedTime, customTimeRange) => {
    const now = moment().format('YYYY-MM-DD');
    const defaultParams = {
      from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      to: moment().format('YYYY-MM-DD')
    };

    if (selectedTime === PAST_30_DAYS) {
      return defaultParams;
    }
    else if (selectedTime === PAST_7_DAYS) {
      return {
        from: moment().subtract(1, 'week').format('YYYY-MM-DD'),
        to: moment().format('YYYY-MM-DD')
      };
    }
    else if (selectedTime === YEAR_TO_DATE) {
      return {
        from: moment().startOf('year').format('YYYY-MM-DD'),
        to: moment().format('YYYY-MM-DD')
      };
    }
    else if (selectedTime === CUSTOM) {
      return {
        from: moment(customTimeRange.startDate).format('YYYY-MM-DD'),
        to: customTimeRange.endDate ? moment(customTimeRange.endDate).format('YYYY-MM-DD') : now
      };
    }

    return defaultParams;
  }
);

export const getHostListingCurrencies = createSelector(
  [getUser, getOwnerId, getOrganizationCurrencies],
  (user, ownerId, orgCurrencies) => {
    if (user && user.listing_currencies.length > 0 && (ownerId === user.id || ownerId === '')) {
      return user.listing_currencies;
    }
    else if (ownerId !== '' && orgCurrencies && orgCurrencies.length > 0) {
      return orgCurrencies;
    }
    return [];
  }
);

export default createStructuredSelector({
  // Reports Home - Quick Reports, etc.
  hasPermissionForReports: getHasPermissionForReports,
  doesNotHavePermissionForReports: getDoesNotHavePermissionForReports,
  // Sourced from account-navbar's selector (the `mumHost` source of truth that
  // drives the Balances tab-hide), NOT this module's local `getHostId`. Keeping
  // them on the same signal means the balance-route redirect and the tab
  // visibility can never disagree about whether the member is on their own
  // account.
  isViewingOwnAccount: getIsViewingOwnAccount,
  customDateStr: getCustomReportDateStr,
  singleDateStr: getSingleReportDateStr,
  reportOptions: getReportOptions,
  showCustomReportDeleteModal: getShowCustomReportModal,
  showLargeReportEmailModal: getShowLargeReportEmailModal,
  showQuickReportModal: getShowQuickReportModal,
  reportFileOptions: getReportFileOptions,
  reportSelected: getReportSelected,
  reportTimeRangeSelected: getReportTimeRangeSelected,
  breakdownTypeSelected: getReportBreakdownTypeSelected,
  reportCustomTimeRange: getReportCustomTimeRange,
  reportFocusedInput: getReportFocusedInput,
  reportSingleDate: getReportSingleDate,
  reportLoading: getReportLoading,
  customReportsPagination: getCustomReportsPagination,
  customReportsSearch: getCustomReportsSearch,
  customReportSelected: getCustomReportSelected,
  includeExtendedAudit: getIncludeExtendedAudit,
  privacyPolicyProvided: getIsPrivacyPolicyProvided,
  waitlistOptInEnabled: getIsWaitlistOptInEnabled,

  // Reports Home - EventSelect modal
  hostId: getHostId,
  selectedEvents: getSelectedEvents,
  selectedEventIDs: getSelectedEventIDs,
  selectedEventsModalIsOpen: getSelectedEventsModalIsOpen,
  dateFilter: getDateFilter,
  selectedTime: getSelectedTime,
  selectedTimeFocusedInput: getSelectedTimeFocusedInput,
  currencies: getHostListingCurrencies,
  customSelectedEvents: getCustomSelectedIDsFromModal,
  selectedCurrency: getSelectedCurrency,

  // Custom Reports - General
  custom: getCustomReport,
  validator: getValidator,
  user: getUser,
  schedule: getCustomReportScheduler,
  amplitudeInstance: getAmplitudeInstance,

  // Custom Reports - Filters + Sorts
  filters: getCustomFilters,
  sorts: getCustomSorts,
  tempTimeRange: getTempTimeRange,
  filterValidator: getFilterValidator,

  // Custom Reports - Date Select modals
  dateOfOrderCustomDateStr: getDateOfOrderCustomDateStr,
  dateOfAttendingCustomDateStr: getDateOfAttendingCustomDateStr,
  dateOfOrderSingleDateStr: getDateOfOrderSingleDateStr,
  dateOfAttendingSingleDateStr: getDateOfAttendingSingleDateStr,
  dateOfOrderAfterDateStr: getDateOfOrderAfterDateStr,
  dateOfAttendingAfterDateStr: getDateOfAttendingAfterDateStr,
  dateOfOrderBeforeDateStr: getDateOfOrderBeforeDateStr,
  dateOfAttendingBeforeDateStr: getDateOfAttendingBeforeDateStr,
  dateOfOrderReportTimeRangeSelected: getDateOfOrderTimeRangeSelected,
  dateOfAttendingReportTimeRangeSelected: getDateOfAttendingTimeRangeSelected,
  dateOfOrderCustomTimeRange: getDateOfOrderCustomTimeRange,
  dateOfAttendingCustomTimeRange: getDateOfAttendingCustomTimeRange,
  dateOfOrderFocusedInput: getDateOfOrderFocusedInput,
  dateOfAttendingFocusedInput: getDateOfAttendingFocusedInput,
  reportDateOfOrderFocusedInput: getDateOfOrderFocusedInput,
  reportDateOfAttendingFocusedInput: getDateOfAttendingFocusedInput,
  dateOfOrderSingleDate: getDateOfOrderSingleDate,
  dateOfAttendingSingleDate: getDateOfAttendingSingleDate,
});



// WEBPACK FOOTER //
// ./src/modules/reports/getters.js